import React, {useEffect, useState} from "react"
import {Link, useParams} from "react-router-dom";
import {ReloadOutlined} from "@ant-design/icons";
import {Button, Card, Col, Descriptions, Empty, Row, Table, Tag} from "antd";
import type {ColumnsType} from "antd/es/table";
import {AxiosError} from "axios";

import {FactoryMetric, FactorySession} from "types";
import {FactoryService} from "services";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import {formatDate, getFailureBinLabel, getFamilyLabel, getStatusColor, getWorkerLabel} from "../helpers";

// ----- Types -----
type RouteParams = {
    sessionId: string;
};

// ----- Components -----
const FactorySessionDetailsPage: React.FC = () => {
    // ----- Params -----
    const {sessionId} = useParams<RouteParams>();

    // ----- Data -----
    const [loading, setLoading] = useState<boolean>(false)
    const [session, setSession] = useState<FactorySession | null>(null)
    const [notFound, setNotFound] = useState<boolean>(false)

    // ----- Init -----
    useEffect(() => {
        fetchData()
    }, [sessionId])

    const fetchData = () => {
        if (!sessionId) return
        setLoading(true)
        setNotFound(false)
        FactoryService.getFactorySession(sessionId)
            .then(res => {
                setSession(res?.data?.session || null)
            })
            .catch((err: AxiosError) => {
                console.error(err)
                // Unknown or malformed id
                if (err?.response?.status === 404 || err?.response?.status === 422) setNotFound(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const columns: ColumnsType<FactoryMetric> = [
        {
            title: 'Metric',
            dataIndex: 'metricId',
            key: 'metricId',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>,
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
        },
        {
            title: 'Limit',
            dataIndex: 'limit',
            key: 'limit',
        },
        {
            title: 'Operator confirmed',
            dataIndex: 'operatorConfirmed',
            key: 'operatorConfirmed',
            render: (operatorConfirmed?: boolean) => operatorConfirmed == null ? '' : (operatorConfirmed ? 'Yes' : 'No'),
        },
        {
            title: 'Fail reason',
            dataIndex: 'failReason',
            key: 'failReason',
        },
    ]

    if (notFound) {
        return (
            <>
                <CustomBreadcrumb list={['factory tests', 'details']}/>
                <Card>
                    <Empty description="Session not found">
                        <Link to="/factory/sessions">Back to factory tests</Link>
                    </Empty>
                </Card>
            </>
        )
    }

    return (
        <>
            <CustomBreadcrumb list={['factory tests', 'details']}/>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title="Session"
                          loading={loading && !session}
                          extra={<Button loading={loading} onClick={() => fetchData()} icon={<ReloadOutlined/>}/>}
                    >
                        {session &&
                            <Descriptions bordered size="small" column={2}>
                                <Descriptions.Item label="Overall">
                                    <Tag color={getStatusColor(session.overall)}>{session.overall?.toUpperCase()}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Family">{getFamilyLabel(session.family)}</Descriptions.Item>
                                <Descriptions.Item label="Failure bin">{getFailureBinLabel(session.failureBin)}</Descriptions.Item>
                                <Descriptions.Item label="Failure note">{session.failureNote}</Descriptions.Item>
                                <Descriptions.Item label="Worker">
                                    <Link to={`/users/${session.worker?.id}`}>{getWorkerLabel(session)}</Link>
                                </Descriptions.Item>
                                <Descriptions.Item label="Started at">{formatDate(session.startedAt)}</Descriptions.Item>
                                <Descriptions.Item label="Saved at">{formatDate(session.createdAt)}</Descriptions.Item>
                                <Descriptions.Item label="Updated at">{formatDate(session.updatedAt)}</Descriptions.Item>
                                <Descriptions.Item label="Session id" span={2}>{session.id}</Descriptions.Item>
                            </Descriptions>
                        }
                    </Card>
                </Col>
                <Col span={24}>
                    <Card title="Device" loading={loading && !session}>
                        {session &&
                            <Descriptions bordered size="small" column={2}>
                                <Descriptions.Item label="Serial">{session.device?.serial}</Descriptions.Item>
                                <Descriptions.Item label="Bluetooth id">{session.device?.bluetoothId}</Descriptions.Item>
                                <Descriptions.Item label="Name">{session.device?.name}</Descriptions.Item>
                                <Descriptions.Item label="Firmware">{session.device?.firmwareVersion}</Descriptions.Item>
                                <Descriptions.Item label="Hardware">{session.device?.hardwareVersion}</Descriptions.Item>
                                <Descriptions.Item label="Battery">
                                    {session.device?.batteryPercent != null && `${session.device.batteryPercent}%`}
                                    {session.device?.batteryMv != null && ` (${session.device.batteryMv} mV)`}
                                </Descriptions.Item>
                            </Descriptions>
                        }
                    </Card>
                </Col>
                <Col span={24}>
                    <Card title="Metrics">
                        <Table<FactoryMetric>
                            rowKey="metricId"
                            columns={columns}
                            dataSource={session?.metrics || []}
                            loading={loading}
                            size="small"
                            bordered
                            pagination={false}
                        />
                    </Card>
                </Col>
            </Row>
        </>
    )
}

export default FactorySessionDetailsPage
